import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@mui/material/styles";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, BarElement, LineElement, ArcElement,
  CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler,
} from "chart.js";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { useGetWellnessDashboardQuery } from "slices/addOnApi";

import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HealingIcon from "@mui/icons-material/Healing";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

ChartJS.register(BarElement, LineElement, ArcElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler);

// ── helpers ───────────────────────────────────────────────────
const TEAL = "#00897B";
const TEAL_LIGHT = "rgba(0,137,123,0.12)";

const SC = ({ children }) => (
  <Typography variant="h6" fontWeight={700} mb={2}
    sx={{ color: "#1a3c34", borderLeft: `4px solid ${TEAL}`, pl: 1.5 }}>
    {children}
  </Typography>
);

const KpiCard = ({ icon, label, value, sub, subColor = "#22c55e", bg = "#fff" }) => (
  <Card sx={{ p: 2.5, borderRadius: 3, height: "100%", background: bg, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}
          textTransform="uppercase" letterSpacing={1}>{label}</Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>{value ?? 0}</Typography>
        {sub && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <TrendingUpIcon sx={{ fontSize: 13, color: subColor }} />
            <Typography variant="caption" fontWeight={600} sx={{ color: subColor }}>{sub}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ background: TEAL_LIGHT, borderRadius: 2, p: 1.2, display: "flex" }}>{icon}</Box>
    </Box>
  </Card>
);

const statusColorMap = {
  active: "success", inactive: "warning", blocked: "error", deleted: "error", pending: "warning",
};
const StatusChip = (s) => (
  <Chip label={s || "—"} size="small" color={statusColorMap[s] || "default"}
    sx={{ fontWeight: 600, fontSize: 11 }} />
);

const TH = ({ cols }) => (
  <TableHead>
    <TableRow sx={{ background: "#e0f2f1" }}>
      {cols.map(c => (
        <TableCell key={c} sx={{ fontWeight: 700, fontSize: 12, color: "#1a3c34", whiteSpace: "nowrap", px: 2, py: 1 }}>
          {c}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const chartOpts = (theme) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: theme.palette.text.primary, font: { size: 11 } } },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    x: { ticks: { color: theme.palette.text.secondary, font: { size: 10 } }, grid: { color: theme.palette.divider } },
    y: { beginAtZero: true, ticks: { color: theme.palette.text.secondary, font: { size: 10 } }, grid: { color: theme.palette.divider } },
  },
});

const doughnutOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: "bottom", labels: { font: { size: 11 } } } },
};

// ── component ─────────────────────────────────────────────────
function WellnessDashboard() {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetWellnessDashboardQuery();

  if (isLoading) return (
    <DashboardLayout><DashboardNavbar />
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
      <Footer />
    </DashboardLayout>
  );

  if (isError || !data) return (
    <DashboardLayout><DashboardNavbar />
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">Failed to load wellness dashboard.</Typography>
      </Box>
      <Footer />
    </DashboardLayout>
  );

  const {
    doctorStats, clinicStats, concernStats, appointmentSummary,
    monthlyAppointments, appointmentsPerConcern, appointmentsPerConsultation,
    clinicConcernLinks, clinicCountPerConcern, latestAppointments, genderBreakdown,
  } = data;

  // ── chart datasets ──────────────────────────────────────────
  const months = monthlyAppointments?.map(d => d.month) || [];

  const appointmentLineData = {
    labels: months,
    datasets: [{
      label: "Appointments",
      data: monthlyAppointments?.map(d => d.count) || [],
      borderColor: TEAL,
      backgroundColor: "rgba(0,137,123,0.15)",
      fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: TEAL,
    }],
  };

  const concernBarData = {
    labels: appointmentsPerConcern?.map(d => d.concernTitle || "—") || [],
    datasets: [{
      label: "Appointments",
      data: appointmentsPerConcern?.map(d => d.count) || [],
      backgroundColor: "rgba(0,137,123,0.75)",
      borderRadius: 4,
    }],
  };

  const consultationBarData = {
    labels: appointmentsPerConsultation?.map(d => d.doctorName || "—") || [],
    datasets: [{
      label: "Appointments",
      data: appointmentsPerConsultation?.map(d => d.count) || [],
      backgroundColor: "rgba(38,166,154,0.75)",
      borderRadius: 4,
    }],
  };

  const genderDoughnutData = {
    labels: ["Male", "Female", "Other"],
    datasets: [{
      data: [genderBreakdown?.male || 0, genderBreakdown?.female || 0, genderBreakdown?.other || 0],
      backgroundColor: ["#42a5f5", "#ec407a", "#ab47bc"],
      borderWidth: 0,
    }],
  };

  const doctorStatusData = {
    labels: ["Active", "Inactive", "Blocked", "Deleted"],
    datasets: [{
      data: [doctorStats?.active || 0, doctorStats?.inactive || 0, doctorStats?.blocked || 0, doctorStats?.deleted || 0],
      backgroundColor: ["#22c55e", "#f59e0b", "#ef4444", "#94a3b8"],
      borderWidth: 0,
    }],
  };

  const clinicStatusData = {
    labels: ["Active", "Inactive", "Blocked", "Deleted"],
    datasets: [{
      data: [clinicStats?.active || 0, clinicStats?.inactive || 0, clinicStats?.blocked || 0, clinicStats?.deleted || 0],
      backgroundColor: ["#22c55e", "#f59e0b", "#ef4444", "#94a3b8"],
      borderWidth: 0,
    }],
  };

  const clinicConcernDoughnutData = {
    labels: clinicCountPerConcern?.map(d => d.concernTitle || "—") || [],
    datasets: [{
      data: clinicCountPerConcern?.map(d => d.clinicCount) || [],
      backgroundColor: ["#00897B","#26a69a","#4db6ac","#80cbc4","#b2dfdb","#e0f2f1","#00695c","#004d40"],
      borderWidth: 0,
    }],
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── KPI ROW 1 — Core counts ── */}
        <Grid container spacing={2.5} mb={3}>
          {[
            { icon: <MedicalServicesIcon sx={{ color: TEAL, fontSize: 26 }} />, label: "Total Doctors", value: doctorStats?.total?.toLocaleString(), sub: `+${doctorStats?.todayReg || 0} today` },
            { icon: <MedicalServicesIcon sx={{ color: "#22c55e", fontSize: 26 }} />, label: "Active Doctors", value: doctorStats?.active?.toLocaleString(), bg: "#f0fdf4" },
            { icon: <MedicalServicesIcon sx={{ color: "#ef4444", fontSize: 26 }} />, label: "Blocked Doctors", value: doctorStats?.blocked?.toLocaleString(), bg: "#fef2f2", subColor: "#ef4444" },
            { icon: <LocalHospitalIcon sx={{ color: TEAL, fontSize: 26 }} />, label: "Total Clinics", value: clinicStats?.total?.toLocaleString(), sub: `+${clinicStats?.todayReg || 0} today` },
            { icon: <LocalHospitalIcon sx={{ color: "#22c55e", fontSize: 26 }} />, label: "Active Clinics", value: clinicStats?.active?.toLocaleString(), bg: "#f0fdf4" },
            { icon: <HealingIcon sx={{ color: TEAL, fontSize: 26 }} />, label: "Total Concerns", value: concernStats?.total?.toLocaleString() },
            { icon: <LinkIcon sx={{ color: "#26a69a", fontSize: 26 }} />, label: "Selected Concerns", value: concernStats?.selectedTotal?.toLocaleString() },
            { icon: <EventNoteIcon sx={{ color: TEAL, fontSize: 26 }} />, label: "Total Appointments", value: appointmentSummary?.total?.toLocaleString(), sub: `+${appointmentSummary?.today || 0} today` },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}><KpiCard {...c} /></Grid>
          ))}
        </Grid>

        {/* ── MONTHLY APPOINTMENTS LINE CHART ── */}
        <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <SC>Monthly Appointments</SC>
          <Box sx={{ height: 280 }}>
            <Line data={appointmentLineData} options={chartOpts(theme)} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {[
              { label: "Total Appointments", value: appointmentSummary?.total || 0, color: TEAL },
              { label: "Today", value: appointmentSummary?.today || 0, color: "#26a69a" },
              { label: "Male", value: genderBreakdown?.male || 0, color: "#42a5f5" },
              { label: "Female", value: genderBreakdown?.female || 0, color: "#ec407a" },
            ].map((s, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ background: "#e0f2f1", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: s.color, mx: "auto", mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
                  <Typography variant="h6" fontWeight={700} color="#1a3c34">{s.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── APPOINTMENTS PER CONCERN BAR + GENDER DOUGHNUT ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Appointments per Concern</SC>
              <Box sx={{ height: 280 }}>
                <Bar data={concernBarData} options={chartOpts(theme)} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Gender Breakdown</SC>
              <Box sx={{ height: 200 }}>
                <Doughnut data={genderDoughnutData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[["Male", genderBreakdown?.male, "#42a5f5"], ["Female", genderBreakdown?.female, "#ec407a"], ["Other", genderBreakdown?.other, "#ab47bc"]].map(([k, v, c]) => (
                <Box key={k} display="flex" justifyContent="space-between" alignItems="center" py={0.4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
                    <Typography variant="caption" fontWeight={600}>{k}</Typography>
                  </Box>
                  <Chip label={v || 0} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />
                </Box>
              ))}
            </Card>
          </Grid>
        </Grid>

        {/* ── APPOINTMENTS PER DOCTOR BAR + DOCTOR STATUS + CLINIC STATUS ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Appointments per Doctor (Top 10)</SC>
              <Box sx={{ height: 280 }}>
                <Bar data={consultationBarData} options={chartOpts(theme)} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Doctor Status</SC>
              <Box sx={{ height: 190 }}>
                <Doughnut data={doctorStatusData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[["Active", doctorStats?.active, "#22c55e"], ["Inactive", doctorStats?.inactive, "#f59e0b"], ["Blocked", doctorStats?.blocked, "#ef4444"], ["Deleted", doctorStats?.deleted, "#94a3b8"]].map(([k, v, c]) => (
                <Box key={k} display="flex" justifyContent="space-between" alignItems="center" py={0.3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
                    <Typography variant="caption" fontWeight={600}>{k}</Typography>
                  </Box>
                  <Chip label={v || 0} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />
                </Box>
              ))}
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Clinic Status</SC>
              <Box sx={{ height: 190 }}>
                <Doughnut data={clinicStatusData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[["Active", clinicStats?.active, "#22c55e"], ["Inactive", clinicStats?.inactive, "#f59e0b"], ["Blocked", clinicStats?.blocked, "#ef4444"], ["Deleted", clinicStats?.deleted, "#94a3b8"]].map(([k, v, c]) => (
                <Box key={k} display="flex" justifyContent="space-between" alignItems="center" py={0.3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
                    <Typography variant="caption" fontWeight={600}>{k}</Typography>
                  </Box>
                  <Chip label={v || 0} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />
                </Box>
              ))}
            </Card>
          </Grid>
        </Grid>

        {/* ── CLINIC COUNT PER CONCERN DOUGHNUT + TABLE ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Clinics per Concern</SC>
              <Box sx={{ height: 220 }}>
                <Doughnut data={clinicConcernDoughnutData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {clinicCountPerConcern?.map((c, i) => (
                <Box key={i} display="flex" justifyContent="space-between" alignItems="center" py={0.4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: TEAL, opacity: 1 - i * 0.1 }} />
                    <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 130 }}>{c.concernTitle || "—"}</Typography>
                  </Box>
                  <Chip label={c.clinicCount} size="small" color="primary" sx={{ fontWeight: 700, fontSize: 11 }} />
                </Box>
              ))}
            </Card>
          </Grid>

          {/* ── CONCERN TABLE with linked key ── */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Concern — Appointments & Clinic Count</SC>
              <TableContainer>
                <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <TH cols={["#", "Concern", "Linked Key", "Commission", "Appointments", "Clinics"]} />
                  <TableBody>
                    {appointmentsPerConcern?.map((c, i) => {
                      const clinicRow = clinicCountPerConcern?.find(cl => cl.concernTitle === c.concernTitle);
                      return (
                        <TableRow key={i} hover>
                          <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" color="text.secondary">{i + 1}</Typography></TableCell>
                          <TableCell sx={{ px: 2, py: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <HealingIcon sx={{ fontSize: 16, color: TEAL, flexShrink: 0 }} />
                              <Typography variant="body2" fontWeight={600} noWrap>{c.concernTitle || "—"}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1 }}>
                            <Chip label={c.concernLinked || "—"} size="small" variant="outlined"
                              sx={{ fontSize: 10, fontWeight: 600, color: TEAL, borderColor: TEAL }} />
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{c.concernCommition ?? "—"}%</Typography>
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1 }}>
                            <Chip label={c.count} size="small" color="primary" sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ px: 2, py: 1 }}>
                            <Chip label={clinicRow?.clinicCount || 0} size="small"
                              sx={{ fontWeight: 700, background: "#e0f2f1", color: "#1a3c34" }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* ── CLINIC — CONCERN LINKS TABLE ── */}
        <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <SC>Clinic — Concern Links</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "5%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <TH cols={["#", "Clinic", "Mobile", "Status", "Concern", "Linked Key"]} />
              <TableBody>
                {clinicConcernLinks?.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" color="text.secondary">{i + 1}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, flexShrink: 0, background: TEAL }}>
                          {r.clinicName?.[0] || "C"}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} noWrap>{r.clinicName || "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" noWrap>{r.clinicMobile || "—"}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>{StatusChip(r.clinicStatus)}</TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <HealingIcon sx={{ fontSize: 14, color: TEAL, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap>{r.concernTitle || "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Chip label={r.concernLinked || "—"} size="small" variant="outlined"
                        sx={{ fontSize: 10, fontWeight: 600, color: TEAL, borderColor: TEAL }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ── LATEST 10 APPOINTMENTS ── */}
        <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <SC>Latest Appointments (Last 10)</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <TH cols={["Patient", "Mobile", "Gender", "Slot", "Concern", "Linked Key", "Doctor", "Date"]} />
              <TableBody>
                {latestAppointments?.map((a, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, flexShrink: 0, background: TEAL }}>
                          {a.name?.[0] || "P"}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500} noWrap>{a.name || "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" noWrap>{a.number || "—"}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Chip label={a.gender || "—"} size="small" variant="outlined"
                        sx={{ fontSize: 10, textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" noWrap>{a.slot || "—"}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <HealingIcon sx={{ fontSize: 13, color: TEAL, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap>{a.concern?.title || "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Chip label={a.concern?.linked || "—"} size="small" variant="outlined"
                        sx={{ fontSize: 10, fontWeight: 600, color: TEAL, borderColor: TEAL }} />
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon sx={{ fontSize: 13, color: "#26a69a", flexShrink: 0 }} />
                        <Typography variant="body2" noWrap>{a.consultation?.name || "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default WellnessDashboard;
